<template>
  <StSlider
    class="sport-tabs"
    :class="{ mini: props.mini }"
    :multiselect="multiselect"
    :selected-ids="selectedIds"
    :arrow-offset-vertical="props.mini ? 0 : 14"
    :scroll-step="72"
    list-tag="ul"
    data-t="sport-tabs"
    :scrollable="!skeletonsCount"
    @select="selectTabHandler"
  >
    <StSliderItem
      v-for="item in allTabs"
      v-slot="{ selected }"
      :key="item.id"
      tag="li"
      class="sport-tab"
      :data-t="`sport-tab-${item.id}`"
    >
      <div
        class="sport-tab-wrapper"
        :title="item.title"
        :class="[{ selected: selected }]"
        data-t="sport-tab-wrapper"
      >
        <StIcon class="sport-tab-icon" :name="item.icon" :size="iconSize" />
      </div>
      <p v-if="!props.mini" class="sport-tab-title" data-t="sport-tab-title">
        {{ item.title }}
      </p>
    </StSliderItem>
    <div
      v-for="slide in skeletonsCount"
      :key="slide"
      class="sport-tab skeleton-slide"
    >
      <div class="sport-tab-wrapper" />
    </div>
  </StSlider>
</template>

<script lang="ts" setup>
import type { SportTab } from './types'

interface TabEmitSelectedParams {
  selectedItemIds: (string | number)[]
  itemId: number | string
}

interface Props {
  items: SportTab[]
  selectedTabsIds?: (string | number)[]
  allTitle?: string
  multiselect?: boolean
  mini?: boolean
  isOnlyOneWithVideo?: boolean
}
const { t } = useI18n()
const emit = defineEmits(['select'])
const props = withDefaults(defineProps<Props>(), {
  selectedTabsIds: () => [],
  multiselect: false,
  allTitle: '',
  mini: false,
  isOnlyOneWithVideo: false,
})

const ALL_TAB_ID = 'all'
const VIDEO_TAB_ID = 'video'
const specialTabs: SportTab[] = [
  {
    id: ALL_TAB_ID,
    title: props.allTitle || t('events.all'),
    icon: 'circle-check-solid',
  },
]

const allTabs = computed<SportTab[]>(() => [...specialTabs, ...props.items])
const selectedIds = ref<(number | string)[]>([ALL_TAB_ID])
const iconSize = computed(() => (props.mini ? 24 : 32))

watch(
  () => props.selectedTabsIds,
  (newIds) => {
    selectedIds.value = newIds.length ? newIds : [ALL_TAB_ID]
  },
  { immediate: true },
)

function emitTabs(
  isAllSelected = true,
  selectedItemIds: (number | string)[] = [],
) {
  const tabs = props.items.filter(
    (item) => selectedItemIds.includes(item.id) && !isAllSelected,
  )

  emit('select', tabs)
}

function multiselectTab({ selectedItemIds, itemId }: TabEmitSelectedParams) {
  const allTabLength = allTabs.value.length
  const selectedTabLength = selectedItemIds.length
  const isAllSelected =
    itemId === ALL_TAB_ID ||
    allTabLength === selectedTabLength + 1 ||
    selectedTabLength === 0

  selectedIds.value = isAllSelected
    ? [ALL_TAB_ID]
    : selectedIds.value.filter((id) => id !== ALL_TAB_ID)

  emitTabs(isAllSelected, selectedItemIds)
}

function selectTab({ selectedItemIds, itemId }: TabEmitSelectedParams) {
  const isAllSelected = itemId === ALL_TAB_ID

  selectedIds.value = isAllSelected ? [ALL_TAB_ID] : [itemId]

  emitTabs(isAllSelected, selectedItemIds)
}

function changeSelected(
  isAllSelected: boolean,
  newTabIds: (string | number)[],
) {
  selectedIds.value = newTabIds
  emitTabs(isAllSelected, selectedIds.value)
}

function selectTabWithVideo({ itemId }: TabEmitSelectedParams) {
  const isAllSelected = itemId === ALL_TAB_ID
  const isVideoSelected = itemId === VIDEO_TAB_ID
  const hasVideo = selectedIds.value.includes(VIDEO_TAB_ID)
  const hasAll = selectedIds.value.includes(ALL_TAB_ID)
  const isTabExists = selectedIds.value.includes(itemId)

  if (isAllSelected) return changeSelected(isAllSelected, [ALL_TAB_ID])

  if (isTabExists) {
    const filteredIds = selectedIds.value.filter((id) => id !== itemId)
    return changeSelected(
      isAllSelected,
      filteredIds.length ? filteredIds : [ALL_TAB_ID],
    )
  }

  if (hasVideo && !isVideoSelected) {
    return changeSelected(isAllSelected, [VIDEO_TAB_ID, itemId])
  }

  if (selectedIds.value.length && !hasAll && !hasVideo && isVideoSelected) {
    return changeSelected(isAllSelected, [...selectedIds.value, itemId])
  }

  return changeSelected(isAllSelected, [itemId])
}

function selectTabHandler(params: TabEmitSelectedParams) {
  if (props.multiselect) return multiselectTab(params)
  if (props.isOnlyOneWithVideo) return selectTabWithVideo(params)
  return selectTab(params)
}

const minSliderItemsCount = computed(() => (props.mini ? 12 : 18))
const skeletonsCount = computed(() => {
  const slidesCount = minSliderItemsCount.value - allTabs.value.length

  return slidesCount > 0 ? slidesCount : 0
})
</script>

<style scoped>
.sport-tab {
  cursor: pointer;

  display: flex;
  flex-direction: column;
  gap: var(--spacing-075);
  align-items: center;
  justify-content: center;

  width: 64px;
}

.sport-tab-icon {
  display: flex;
  color: var(--text-tertiary);
}

.sport-tab-title {
  overflow: hidden;

  width: calc(100% - var(--spacing-050));
  margin: 0;

  font: var(--desktop-text-xxs-medium);
  color: var(--text-tertiary);
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sport-tab-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 64px;
  padding: var(--spacing-200);

  background: var(--background-primary);
  border-radius: var(--border-radius-150);

  &.selected {
    background: var(--background-secondary);

    .sport-tab-icon {
      color: var(--text-link);
    }

    + .sport-tab-title {
      color: var(--text-link);
    }
  }

  &:hover {
    &:not(.selected) {
      background-color: var(--background-tertiary);

      .sport-tab-icon {
        color: var(--text-primary);
      }

      + .sport-tab-title {
        color: var(--text-primary);
      }
    }
  }
}

.sport-tabs {
  &.mini {
    .sport-tab {
      width: 48px;
    }

    .sport-tab-wrapper {
      height: 48px;
      padding: var(--spacing-150);
    }
  }
}

.skeleton-slide {
  pointer-events: none;
  opacity: 0.4;
}
</style>
